import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import SvgIcon from '../SvgIcon'
import paddingIcon from '../../assets/svg/paddingIcon.svg'
import successIcon from '../../assets/svg/successIcon.svg'
import errorIcon from '../../assets/svg/errorIcon.svg'
import warningIcon from '../../assets/svg/warningIcon.svg' 
import closeIcon from '../../assets/svg/closeIcon.svg'

// Toast 类型定义 - 添加警告状态
export type ToastType = 'success' | 'error' | 'warning' | 'pending';

// Toast 配置 - 简化为单行文字
export interface ToastProps {
  type: ToastType;
  message: string; // 只保留一个 message 字段
  icon?: React.ReactNode; // 自定义图标（可选）
  duration?: number; // pending 类型会忽略此参数
  onClose?: () => void;
}

// Toast 实例接口
interface ToastInstance {
  id: string;
  content: React.ReactNode;
}

// 默认图标组件
const DefaultIcons = {
  success: (
    <div className="flex items-center justify-center w-5 h-5 text-green-500">
      <SvgIcon src={successIcon} size={20}/>
    </div>
  ),
  error: (
    <div className="flex items-center justify-center w-5 h-5 text-red-500">
      <SvgIcon src={errorIcon} size={20}/>
    </div>
  ),
  warning: (
    <div className="flex items-center justify-center w-5 h-5 text-yellow-500">
      <SvgIcon src={warningIcon} size={20}/>
    </div>
  ),
  pending: (
    <div className="flex items-center justify-center w-5 h-5 text-blue-500">
      <div className="animate-spin">
        <SvgIcon src={paddingIcon} size={20}/>
      </div>
    </div>
  )
};

// Toast 单一实例组件
const ToastItem: React.FC<ToastProps & { onDismiss: () => void }> = ({ 
  type, 
  message,
  icon, 
  duration = 3000, 
  onClose, 
  onDismiss 
}) => {
  const [visible, setVisible] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const startXRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 判断是否为 pending 类型（不自动消失）
  const isPending = type === 'pending';
  
  // 显示自定义图标或默认图标
  const renderIcon = () => {
    // 如果传入了自定义图标，使用自定义图标
    if (icon !== undefined) {
      return icon;
    }
    // 否则使用默认图标
    return DefaultIcons[type];
  };
  
  // 处理关闭
  const handleClose = () => {
    setVisible(false);
    // 清除定时器
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setTimeout(() => {
      onDismiss();
      onClose?.();
    }, 300);
  };
  
  // 自动消失 - pending 类型不会自动消失
  useEffect(() => {
    if (isPending) return; // pending 类型不设置定时器
    
    timerRef.current = setTimeout(() => {
      handleClose();
    }, duration);
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [duration, isPending]);
  
  // 鼠标/触摸事件处理
  const handlePointerDown = (e: React.PointerEvent) => {
    setSwiping(true);
    startXRef.current = e.clientX;
  };
  
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!swiping) return;
    
    const deltaX = e.clientX - startXRef.current;
    if (deltaX > 0) {
      setSwipeX(deltaX);
    }
  };
  
  const handlePointerUp = () => {
    if (swiping) {
      if (swipeX > 100) {
        handleClose();
      } else {
        setSwipeX(0);
      }
      setSwiping(false);
    }
  };
  
  const handlePointerLeave = () => {
    if (swiping) {
      setSwipeX(0);
      setSwiping(false);
    }
  };
  
  return (
    <div 
      className={`bg-button dark:bg-button rounded-lg shadow-lg border border-normal ${
        visible ? 'animate-slide-in-right' : 'animate-slide-out-right'
      }`}
      style={{ 
        transform: `translateX(${swipeX}px)`, 
        transition: swiping ? 'none' : 'transform 0.3s ease',
        minWidth: '280px',
        maxWidth: '400px'
      }} 
      onPointerDown={handlePointerDown} 
      onPointerMove={handlePointerMove} 
      onPointerUp={handlePointerUp} 
      onPointerLeave={handlePointerLeave} 
      onTouchStart={(e) => e.preventDefault()} // 防止滚动
    >
      <div className="flex items-center px-4 py-3">
        {/* 图标 */}
        <div className="flex-shrink-0">
          {renderIcon()}
        </div>
        
        {/* 内容 - 单行文字 */}
        <div className="ml-3 flex-1 overflow-hidden">
          <p className="text-xs text-reverse dark:text-reverse truncate">
            {message}
          </p>
        </div>
        
        {/* 关闭按钮 */}
        <button 
          className="ml-3 flex-shrink-0 text-tertiary hover:text-primary dark:text-dark-primary transition-colors" 
          onClick={handleClose}
        >
          <SvgIcon src={closeIcon} size={16}/>
        </button>
      </div>
    </div>
  );
};

// Toast 容器
const ToastContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="fixed bottom-4 right-4 z-50 pointer-events-none">
      <div className="pointer-events-auto flex flex-col space-y-2 max-w-md">
        {children}
      </div>
    </div>
  );
};

// 全局单例 Toast 服务
class ToastService {
  private static toasts: ToastInstance[] = [];
  private static listeners: Set<() => void> = new Set();
  private static counter = 0;
  
  // 获取 toasts
  static getToasts() {
    return [...this.toasts];
  }
  
  // 添加 toast
  static addToast(props: ToastProps): string {
    const id = `toast-${++this.counter}`;
    
    const dismiss = () => this.removeToast(id);
    
    const content = (
      <ToastItem
        key={id}
        {...props}
        onDismiss={dismiss}
      />
    );
    
    this.toasts.push({ id, content });
    this.notifyListeners();
    
    return id;
  }
  
  // 移除 toast
  static removeToast(id: string) {
    const initialLength = this.toasts.length;
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notifyListeners();
  }
  
  // 移除所有 toast
  static clearToasts() {
    this.toasts = [];
    this.notifyListeners();
  }
  
  // 订阅变更
  static subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  
  // 通知监听器
  private static notifyListeners() {
    this.listeners.forEach(listener => listener());
  }
  
  // 快捷方法 - 成功
  static success(options: Omit<ToastProps, 'type'>) {
    return this.addToast({ ...options, type: 'success' });
  }
  
  // 快捷方法 - 错误
  static error(options: Omit<ToastProps, 'type'>) {
    return this.addToast({ ...options, type: 'error' });
  }
  
  // 快捷方法 - 警告
  static warning(options: Omit<ToastProps, 'type'>) {
    return this.addToast({ ...options, type: 'warning' });
  }
  
  // 快捷方法 - 等待中
  static pending(options: Omit<ToastProps, 'type'>) {
    return this.addToast({ ...options, type: 'pending' });
  }
}

// Toast 组件 - 用于渲染所有 Toast
const Toast: React.FC = () => {
  const [toasts, setToasts] = useState<ToastInstance[]>([]);
  
  useEffect(() => {
    // 初始状态
    setToasts(ToastService.getToasts());
    
    // 订阅变更
    const unsubscribe = ToastService.subscribe(() => {
      setToasts(ToastService.getToasts());
    });
    
    return unsubscribe;
  }, []);
  
  // 使用 createPortal 挂载到 body
  return createPortal(
    <ToastContainer>
      {toasts.map(toast => toast.content)}
    </ToastContainer>,
    document.body
  );
};
 
// React Hook
function useToast() {
  const toastRef = useRef({
    success: (options: Omit<ToastProps, 'type'>) => ToastService.success(options),
    error: (options: Omit<ToastProps, 'type'>) => ToastService.error(options),
    warning: (options: Omit<ToastProps, 'type'>) => ToastService.warning(options),
    pending: (options: Omit<ToastProps, 'type'>) => ToastService.pending(options),
    close: (id: string) => {
      ToastService.removeToast(id);
    },
    closeAll: () => ToastService.clearToasts()
  });

  return toastRef.current;
}

// 对外暴露
export { Toast, ToastService, useToast };

// 为全局使用添加 CSS 动画
const injectGlobalStyles = () => {
  if (typeof document !== 'undefined' && !document.getElementById('toast-styles')) {
    const style = document.createElement('style');
    style.id = 'toast-styles';
    style.innerHTML = `
      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes slideOutRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
      
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
      
      .animate-slide-in-right {
        animation: slideInRight 0.3s ease-out forwards;
      }
      
      .animate-slide-out-right {
        animation: slideOutRight 0.3s ease-out forwards;
      }
      
      .animate-spin {
        animation: spin 1s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }
};

// 在客户端环境下注入样式
if (typeof window !== 'undefined') {
  injectGlobalStyles();
}